"use client";

import { useRouter, usePathname, useSearchParams } from "next/navigation";
import React from "react";

import { useTranslations } from "next-intl";

import IconClearFilter from "@/icons/ui/buttons/close-with-circle.svg";
import { useCurrencyStore } from "@/store/currency.store";
import { Button } from "@/ui/Button";
import { Divider } from "@/ui/Divider";
import { cn } from "@/utils/cn";
import { PlansFilterParams, hasActiveFilters } from "@/utils/filterLayouts";
import { FilterPrice } from "@/widgets/Filter/Price/FilterPrice";
import { FilterRooms } from "@/widgets/Filter/Rooms/FilterRooms";
import { SortSelect } from "@/widgets/Filter/Sort";

import { FilterArea } from "../Area/FilterArea";

import styles from "./style.module.scss";

interface FilterPanelProps {
  currentFilters: PlansFilterParams;
  itemsCount: number;
  className?: string;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  currentFilters,
  itemsCount,
  className,
}) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { currency, formatCurrencySymbol } = useCurrencyStore();

  const clearAllFilters = () => {
    const params = new URLSearchParams(searchParams.toString());

    // Remove all filter parameters
    const allParams = Array.from(params.keys());
    allParams.forEach((key) => {
      if (
        key === "rooms[]" ||
        key === "minPrice" ||
        key === "maxPrice" ||
        key === "areaMin" ||
        key === "areaMax" ||
        key === "sortBy"
      ) {
        params.delete(key);
      }
    });

    router.push(`${pathname}?${params.toString()}`);
  };

  const isFiltersActive = hasActiveFilters(currentFilters);

  return (
    <div className={cn(styles.filterPanel, className)}>
      <div className={styles.filtersGrid}>
        <FilterRooms
          currentFilters={currentFilters}
          className={styles.filterItem}
        />

        <FilterPrice
          currentFilters={currentFilters}
          className={styles.filterItem}
          currencySymbol={formatCurrencySymbol(currency.value)}
        />

        <FilterArea
          currentFilters={currentFilters}
          className={styles.filterItem}
        />
      </div>

      <div className={styles.footer}>
        <div className={"order-2 text-sm md:text-md sm:me-auto"}>
          {t("Found")} {t("pluralization.plan", { count: itemsCount })}
        </div>

        {isFiltersActive && (
          <Button
            className={"px-6 w-full sm:w-auto order-1 md:order-2"}
            radius="md"
            color="white"
            onClick={clearAllFilters}
          >
            <span className="truncate">{t("Clear all")}</span>
            <IconClearFilter className="w-[2rem] h-[2rem] shrink-0" />
          </Button>
        )}

        <SortSelect
          currentSort={currentFilters.sortBy || ""}
          className={cn(styles.filterItem, "order-2")}
        />
      </div>

      <Divider />
    </div>
  );
};
